"use client";
import React, { useState, useEffect } from "react";
import {
  X,
  BookOpen,
  Play,
  FileText,
  Clock,
  Users,
  ChevronRight,
  Download,
  CheckCircle,
  Circle,
  Sparkles,
  Plus,
  Trash2,
  Upload,
  Edit,
  Save,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Award,
  Video,
  AlertCircle
} from "lucide-react";
import { useCourseContent } from "../context/CourseContentContext";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
    purple: "bg-purple-50 text-purple-700 ring-purple-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

// Initial data structure
const getInitialCourseContent = () => ({
  modules: [
    {
      id: Date.now(),
      title: "",
      concepts: [
        {
          id: Date.now() + 1,
          title: "",
          type: "video",
          file: null,
          duration: ""
        }
      ],
      mcqTest: {
        title: "",
        questions: [
          {
            id: Date.now() + 2,
            question: "",
            options: ["", "", "", ""],
            correctAnswer: 0
          }
        ]
      }
    }
  ],
  finalTest: {
    title: "Final Course Test",
    questions: [
      {
        id: Date.now() + 3,
        question: "",
        options: ["", "", "", ""],
        correctAnswer: 0
      }
    ]
  }
});

export default function CourseContentModal({ course, onClose }) {
  const {
    courseContent,
    loading,
    error,
    fetchCourseContent,
    createCourseContent,
    updateCourseContent,
    deleteCourseContent,
    clearContent
  } = useCourseContent();

  const [localContent, setLocalContent] = useState(getInitialCourseContent());
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [expandedTests, setExpandedTests] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [hasExistingContent, setHasExistingContent] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (course?.id && !dataLoaded) {
      // Load existing course content only once
      const loadContent = async () => {
        setInitialLoading(true);
        try {
          const content = await fetchCourseContent(course.id);
          if (content && content.modules && content.modules.length > 0 && content.modules[0].title) {
            setLocalContent(content);
            setHasExistingContent(true);
            // Expand first module by default
            if (content.modules[0]) {
              setExpandedModules(new Set([content.modules[0].id]));
            }
          } else {
            // No existing content, use initial structure
            const initialContent = getInitialCourseContent();
            setLocalContent(initialContent);
            setHasExistingContent(false);
            setExpandedModules(new Set([initialContent.modules[0].id]));
          }
        } catch (err) {
          console.error("Error loading content:", err);
          // Fallback to initial content
          const initialContent = getInitialCourseContent();
          setLocalContent(initialContent);
          setHasExistingContent(false);
          setExpandedModules(new Set([initialContent.modules[0].id]));
        } finally {
          setInitialLoading(false);
          setDataLoaded(true);
        }
      };

      loadContent();
    }

    // Cleanup on unmount
    return () => {
      if (!course?.id) {
        clearContent();
        setDataLoaded(false);
      }
    };
  }, [course?.id, dataLoaded, fetchCourseContent, clearContent]);

  const toggleModule = (moduleId) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const toggleTest = (testId) => {
    const newExpanded = new Set(expandedTests);
    if (newExpanded.has(testId)) {
      newExpanded.delete(testId);
    } else {
      newExpanded.add(testId);
    }
    setExpandedTests(newExpanded);
  };

  // Module management functions
  const addModule = () => {
    const newModule = {
      id: Date.now(),
      title: "",
      concepts: [
        {
          id: Date.now() + 1,
          title: "",
          type: "video",
          file: null,
          duration: ""
        }
      ],
      mcqTest: {
        title: "",
        questions: [
          {
            id: Date.now() + 2,
            question: "",
            options: ["", "", "", ""],
            correctAnswer: 0
          }
        ]
      }
    };
    setLocalContent(prev => ({
      ...prev,
      modules: [...prev.modules, newModule]
    }));
    setExpandedModules(prev => new Set([...prev, newModule.id]));
  };

  const removeModule = (moduleId) => {
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.filter(m => m.id !== moduleId)
    }));
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      newSet.delete(moduleId);
      return newSet;
    });
  };

  const updateModule = (moduleId, field, value) => {
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId ? { ...m, [field]: value } : m
      )
    }));
  };

  // Concept management functions
  const addConcept = (moduleId) => {
    const newConcept = {
      id: Date.now(),
      title: "",
      type: "video",
      file: null,
      duration: ""
    };
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId
          ? { ...m, concepts: [...m.concepts, newConcept] }
          : m
      )
    }));
  };

  const removeConcept = (moduleId, conceptId) => {
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId
          ? { ...m, concepts: m.concepts.filter(c => c.id !== conceptId) }
          : m
      )
    }));
  };

  const updateConcept = (moduleId, conceptId, field, value) => {
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              concepts: m.concepts.map(c =>
                c.id === conceptId ? { ...c, [field]: value } : c
              )
            }
          : m
      )
    }));
  };

  // MCQ Test management functions
  const addMCQQuestion = (moduleId) => {
    const newQuestion = {
      id: Date.now(),
      question: "",
      options: ["", "", "", ""],
      correctAnswer: 0
    };
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              mcqTest: {
                ...m.mcqTest,
                questions: [...m.mcqTest.questions, newQuestion]
              }
            }
          : m
      )
    }));
  };

  const removeMCQQuestion = (moduleId, questionId) => {
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              mcqTest: {
                ...m.mcqTest,
                questions: m.mcqTest.questions.filter(q => q.id !== questionId)
              }
            }
          : m
      )
    }));
  };

  const updateMCQQuestion = (moduleId, questionId, field, value) => {
    setLocalContent(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              mcqTest: {
                ...m.mcqTest,
                questions: m.mcqTest.questions.map(q =>
                  q.id === questionId ? { ...q, [field]: value } : q
                )
              }
            }
          : m
      )
    }));
  };

  // Final test management functions
  const addFinalTestQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      question: "",
      options: ["", "", "", ""],
      correctAnswer: 0
    };
    setLocalContent(prev => ({
      ...prev,
      finalTest: {
        ...prev.finalTest,
        questions: [...prev.finalTest.questions, newQuestion]
      }
    }));
  };

  const removeFinalTestQuestion = (questionId) => {
    setLocalContent(prev => ({
      ...prev,
      finalTest: {
        ...prev.finalTest,
        questions: prev.finalTest.questions.filter(q => q.id !== questionId)
      }
    }));
  };

  const updateFinalTestQuestion = (questionId, field, value) => {
    setLocalContent(prev => ({
      ...prev,
      finalTest: {
        ...prev.finalTest,
        questions: prev.finalTest.questions.map(q =>
          q.id === questionId ? { ...q, [field]: value } : q
        )
      }
    }));
  };

  const handleSave = async () => {
  if (!course?.id) {
    alert("Course ID is required");
    return;
  }

  setSaving(true);
  try {
    let success = false;

    if (hasExistingContent) {
      success = await updateCourseContent(course.id, localContent);
    } else {
      success = await createCourseContent(course.id, localContent);
    }

    if (success) {
      alert("Course content saved successfully!");
      setHasExistingContent(true);

      // ✅ Close the modal after successful save
      onClose(); 
    } else {
      alert(error || "Failed to save course content");
    }
  } catch (err) {
    console.error("Save error:", err);
    alert("An error occurred while saving");
  } finally {
    setSaving(false);
  }
};


  const handleDelete = async () => {
    if (!course?.id || !hasExistingContent) return;

    const success = await deleteCourseContent(course.id);
    if (success) {
      alert("Course content deleted successfully!");
      const initialContent = getInitialCourseContent();
      setLocalContent(initialContent);
      setHasExistingContent(false);
      setExpandedModules(new Set([initialContent.modules[0].id]));
    } else {
      alert(error || "Failed to delete course content");
    }
  };

  if (!course) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-6xl bg-white rounded-3xl ring-1 ring-gray-200 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-red-50 to-orange-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Pill tone="red">
                  <Edit className="h-4 w-4 mr-1" />
                  Content Management
                </Pill>
                <Pill tone="blue">
                  <Sparkles className="h-4 w-4 mr-1" />
                  Admin Portal
                </Pill>
                {hasExistingContent && (
                  <Pill tone="green">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Content Exists
                  </Pill>
                )}
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
                Manage Course Content: {course.name}
              </h2>
              <p className="text-gray-600 text-sm">
                Create and organize modules, concepts, and assessments for this course
              </p>
              {error && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-2xl hover:bg-gray-100 transition-colors flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {initialLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-red-600 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600">Loading course content...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Modules Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-red-600" />
                    Course Modules
                  </h3>
                  <button
                    onClick={addModule}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-red-600 text-white hover:bg-red-700 transition-colors font-extrabold text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Module
                  </button>
                </div>

                {localContent.modules.map((module, moduleIndex) => (
                  <div key={module.id} className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
                    <div className="p-6 bg-gray-50 border-b border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleModule(module.id)}
                            className="p-2 rounded-xl hover:bg-gray-200 transition-colors"
                          >
                            {expandedModules.has(module.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                          <h4 className="font-extrabold text-gray-900">
                            Module {moduleIndex + 1}
                          </h4>
                        </div>
                        <button
                          onClick={() => removeModule(module.id)}
                          className="p-2 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-extrabold text-gray-700 mb-2">
                            Module Title
                          </label>
                          <input
                            type="text"
                            value={module.title}
                            onChange={(e) => updateModule(module.id, 'title', e.target.value)}
                            placeholder="Enter module title"
                            className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {expandedModules.has(module.id) && (
                      <div className="p-6 space-y-6">
                        {/* Concepts Section */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="font-extrabold text-gray-900 flex items-center gap-2">
                              <Video className="h-4 w-4 text-blue-600" />
                              Video Concepts
                            </h5>
                            <button
                              onClick={() => addConcept(module.id)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                              <Plus className="h-3 w-3" />
                              Add Video
                            </button>
                          </div>

                          <div className="space-y-4">
                            {module.concepts.map((concept, conceptIndex) => (
                              <div key={concept.id} className="p-4 bg-blue-50 rounded-2xl ring-1 ring-blue-100">
                                <div className="flex items-center justify-between mb-3">
                                  <h6 className="font-medium text-gray-900 flex items-center gap-2">
                                    <Video className="h-4 w-4 text-blue-600" />
                                    Video {conceptIndex + 1}
                                  </h6>
                                  <button
                                    onClick={() => removeConcept(module.id, concept.id)}
                                    className="p-1 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Video Title
                                    </label>
                                    <input
                                      type="text"
                                      value={concept.title}
                                      onChange={(e) => updateConcept(module.id, concept.id, 'title', e.target.value)}
                                      placeholder="Enter video title"
                                      className="w-full px-3 py-2 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Duration (optional)
                                    </label>
                                    <input
                                      type="text"
                                      value={concept.duration || ""}
                                      onChange={(e) => updateConcept(module.id, concept.id, 'duration', e.target.value)}
                                      placeholder="e.g., 10:30"
                                      className="w-full px-3 py-2 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Upload Video
                                    </label>
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="file"
                                        accept="video/*"
                                        onChange={(e) => updateConcept(module.id, concept.id, 'file', e.target.files[0])}
                                        className="flex-1 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                      />
                                      <Pill tone="blue" size="sm">
                                        <Play className="h-3 w-3 mr-1" />
                                        Video
                                      </Pill>
                                    </div>
                                    {concept.file && (
                                      <div className="mt-2 text-sm text-gray-600">
                                        {concept.file instanceof File ? (
                                          <span className="flex items-center gap-1">
                                            <CheckCircle className="h-3 w-3 text-green-600" />
                                            New file: {concept.file.name}
                                          </span>
                                        ) : (
                                          <span className="flex items-center gap-1">
                                            <CheckCircle className="h-3 w-3 text-blue-600" />
                                            Existing video uploaded
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* MCQ Test Section */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="font-extrabold text-gray-900 flex items-center gap-2">
                              <HelpCircle className="h-4 w-4 text-purple-600" />
                              Module Test (MCQ)
                            </h5>
                            <button
                              onClick={() => toggleTest(`module-${module.id}`)}
                              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                            >
                              {expandedTests.has(`module-${module.id}`) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                          </div>

                          {expandedTests.has(`module-${module.id}`) && (
                            <div className="p-4 bg-purple-50 rounded-2xl ring-1 ring-purple-100 space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Test Title
                                </label>
                                <input
                                  type="text"
                                  value={module.mcqTest.title}
                                  onChange={(e) => updateModule(module.id, 'mcqTest', { ...module.mcqTest, title: e.target.value })}
                                  placeholder="Enter test title"
                                  className="w-full px-3 py-2 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                                />
                              </div>

                              <div className="flex items-center justify-between">
                                <h6 className="font-medium text-gray-900">Questions</h6>
                                <button
                                  onClick={() => addMCQQuestion(module.id)}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 transition-colors font-medium text-sm"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add Question
                                </button>
                              </div>

                              {module.mcqTest.questions.map((question, qIndex) => (
                                <div key={question.id} className="p-3 bg-white rounded-xl ring-1 ring-gray-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="font-medium text-gray-900">Question {qIndex + 1}</span>
                                    <button
                                      onClick={() => removeMCQQuestion(module.id, question.id)}
                                      className="p-1 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>

                                  <div className="space-y-3">
                                    <input
                                      type="text"
                                      value={question.question}
                                      onChange={(e) => updateMCQQuestion(module.id, question.id, 'question', e.target.value)}
                                      placeholder="Enter question"
                                      className="w-full px-3 py-2 rounded-lg ring-1 ring-gray-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {question.options.map((option, optIndex) => (
                                        <div key={optIndex} className="flex items-center gap-2">
                                          <input
                                            type="radio"
                                            name={`question-${question.id}`}
                                            checked={question.correctAnswer === optIndex}
                                            onChange={() => updateMCQQuestion(module.id, question.id, 'correctAnswer', optIndex)}
                                            className="text-purple-600"
                                          />
                                          <input
                                            type="text"
                                            value={option}
                                            onChange={(e) => {
                                              const newOptions = [...question.options];
                                              newOptions[optIndex] = e.target.value;
                                              updateMCQQuestion(module.id, question.id, 'options', newOptions);
                                            }}
                                            placeholder={`Option ${optIndex + 1}`}
                                            className="flex-1 px-3 py-2 rounded-lg ring-1 ring-gray-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Final Course Test Section */}
              <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
                <div className="p-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                      <Award className="h-5 w-5 text-amber-600" />
                      Final Course Test
                    </h3>
                    <button
                      onClick={() => toggleTest('final')}
                      className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      {expandedTests.has('final') ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {expandedTests.has('final') && (
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-extrabold text-gray-700 mb-2">
                        Final Test Title
                      </label>
                      <input
                        type="text"
                        value={localContent.finalTest.title}
                        onChange={(e) => setLocalContent(prev => ({
                          ...prev,
                          finalTest: { ...prev.finalTest, title: e.target.value }
                        }))}
                        placeholder="Enter final test title"
                        className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-amber-500 outline-none"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <h4 className="font-extrabold text-gray-900">Questions</h4>
                      <button
                        onClick={addFinalTestQuestion}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-amber-600 text-white hover:bg-amber-700 transition-colors font-extrabold text-sm"
                      >
                        <Plus className="h-4 w-4" />
                        Add Question
                      </button>
                    </div>

                    <div className="space-y-4">
                      {localContent.finalTest.questions.map((question, qIndex) => (
                        <div key={question.id} className="p-4 bg-amber-50 rounded-2xl ring-1 ring-amber-100">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-extrabold text-gray-900">Question {qIndex + 1}</span>
                            <button
                              onClick={() => removeFinalTestQuestion(question.id)}
                              className="p-2 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="space-y-3">
                            <input
                              type="text"
                              value={question.question}
                              onChange={(e) => updateFinalTestQuestion(question.id, 'question', e.target.value)}
                              placeholder="Enter question"
                              className="w-full px-4 py-3 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-amber-500 outline-none"
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center gap-3">
                                  <input
                                    type="radio"
                                    name={`final-question-${question.id}`}
                                    checked={question.correctAnswer === optIndex}
                                    onChange={() => updateFinalTestQuestion(question.id, 'correctAnswer', optIndex)}
                                    className="text-amber-600"
                                  />
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) => {
                                      const newOptions = [...question.options];
                                      newOptions[optIndex] = e.target.value;
                                      updateFinalTestQuestion(question.id, 'options', newOptions);
                                    }}
                                    placeholder={`Option ${optIndex + 1}`}
                                    className="flex-1 px-3 py-2 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-amber-500 outline-none"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {hasExistingContent ? "Content exists - changes will update existing data" : "No content exists - will create new content"}
            </div>
            <div className="flex items-center gap-3">
              {hasExistingContent && (
                <button
                  onClick={handleDelete}
                  disabled={loading || saving || initialLoading}
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-red-50 text-red-700 hover:bg-red-100 transition-colors ring-1 ring-red-200 font-medium"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All Content
                </button>
              )}
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-white text-gray-700 hover:bg-gray-100 transition-colors ring-1 ring-gray-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading || saving || initialLoading}
                className={cx(
                  "inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-extrabold transition-colors",
                  loading || saving || initialLoading
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                )}
              >
                {saving ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving ? "Saving..." : hasExistingContent ? "Update Content" : "Save Content"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
